import Modal from '@/components/base/Modal';
import type { AuditLog } from '@/services/audit';

const ACTION_MAP: Record<string, { label: string; color: string; bg: string }> = {
  CREATE: { label: 'Creación', color: 'text-emerald-700', bg: 'bg-emerald-50' },
  UPDATE: { label: 'Edición', color: 'text-amber-700', bg: 'bg-amber-50' },
  DELETE: { label: 'Eliminación', color: 'text-red-700', bg: 'bg-red-50' },
};

const ENTITY_MAP: Record<string, string> = {
  products: 'Producto',
  orders: 'Pedido',
  sales: 'Venta',
  inventory: 'Inventario',
  settings: 'Configuración',
  shipping_rates: 'Tarifas de envío',
  consignments: 'Consignación',
  customers: 'Cliente',
  distributors: 'Distribuidor',
  suppliers: 'Proveedor',
  purchases: 'Compra',
  expenses: 'Gasto',
  payments: 'Pago',
  commissions: 'Comisión',
  donations: 'Donación',
  returns: 'Devolución',
  exchanges: 'Cambio',
  warranties: 'Garantía',
  campaigns: 'Campaña',
  documents: 'Documento',
  sizes: 'Talla',
  users: 'Usuario',
  salespersons: 'Vendedor',
  roles: 'Rol',
  testimonials: 'Testimonios',
};

function entityLabel(type: string): string {
  return ENTITY_MAP[type] ?? type;
}

function actionMeta(action: string) {
  return (
    ACTION_MAP[action] ?? {
      label: action,
      color: 'text-foreground-600',
      bg: 'bg-background-100',
    }
  );
}

function formatDate(value: string): string {
  if (!value) return '—';
  return new Date(value).toLocaleString('es-CO', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

interface Props {
  log: AuditLog;
  onClose: () => void;
}

export default function AuditDetailModal({ log, onClose }: Props) {
  const meta = actionMeta(log.action);

  const hasField = Boolean(log.fieldName);
  const hasOld = log.oldValue !== null && log.oldValue !== undefined;
  const hasNew = log.newValue !== null && log.newValue !== undefined;

  return (
    <Modal
      title="Detalle de auditoría"
      subtitle={`${entityLabel(log.entityType)} · ${meta.label}`}
      onClose={onClose}
      maxWidth="max-w-lg"
      footer={
        <button
          onClick={onClose}
          className="px-4 py-2 text-sm font-medium rounded-md bg-background-100 text-foreground-700 hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer"
        >
          Cerrar
        </button>
      }
    >
      <div className="space-y-5">
        {/* Metadatos del evento */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="rounded-lg border border-background-200 p-3">
            <p className="text-[11px] text-foreground-500">Acción</p>
            <span
              className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium mt-1 whitespace-nowrap ${meta.color} ${meta.bg}`}
            >
              {meta.label}
            </span>
          </div>
          <div className="rounded-lg border border-background-200 p-3">
            <p className="text-[11px] text-foreground-500">Entidad</p>
            <p className="text-sm font-medium text-foreground-900 mt-1">{entityLabel(log.entityType)}</p>
          </div>
          <div className="rounded-lg border border-background-200 p-3">
            <p className="text-[11px] text-foreground-500">Usuario</p>
            <p className="text-sm font-medium text-foreground-900 mt-1">
              {log.userName || 'Sistema'}
            </p>
            {log.userEmail && (
              <p className="text-[11px] text-foreground-500 mt-0.5 truncate">{log.userEmail}</p>
            )}
          </div>
          <div className="rounded-lg border border-background-200 p-3">
            <p className="text-[11px] text-foreground-500">Fecha</p>
            <p className="text-sm font-medium text-foreground-900 mt-1">{formatDate(log.createdAt)}</p>
          </div>
        </div>

        {/* Campo afectado */}
        {hasField && (
          <div>
            <p className="text-[11px] text-foreground-500 mb-1.5">Campo afectado</p>
            <p className="text-sm font-medium text-foreground-900 bg-background-50 rounded-md px-3 py-2 border border-background-200">
              {log.fieldName}
            </p>
          </div>
        )}

        {/* Cambio old -> new */}
        <div>
          <p className="text-[11px] text-foreground-500 mb-2">Cambio</p>
          {log.action === 'CREATE' ? (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
              <p className="text-[11px] text-emerald-600 font-medium mb-1">Valor creado</p>
              <p className="text-sm text-emerald-900 break-words whitespace-pre-wrap">
                {hasNew ? log.newValue : '—'}
              </p>
            </div>
          ) : log.action === 'DELETE' ? (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3">
              <p className="text-[11px] text-red-600 font-medium mb-1">Valor eliminado</p>
              <p className="text-sm text-red-900 break-words whitespace-pre-wrap">
                {hasOld ? log.oldValue : '—'}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                <p className="text-[11px] text-amber-700 font-medium mb-1">Antes</p>
                <p className="text-sm text-amber-900 break-words whitespace-pre-wrap font-mono">
                  {hasOld ? log.oldValue : '—'}
                </p>
              </div>
              <div className="flex items-center justify-center">
                <i className="ri-arrow-down-line text-foreground-400"></i>
              </div>
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                <p className="text-[11px] text-emerald-700 font-medium mb-1">Después</p>
                <p className="text-sm text-emerald-900 break-words whitespace-pre-wrap font-mono">
                  {hasNew ? log.newValue : '—'}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}